'use client';

import React, { createContext, useContext, useCallback, useMemo } from 'react';
type StorageType = 'local' | 'session';

/**
 * Default key prefix. Kept as `sizzlenz_` so existing Sizzle browser storage keeps
 * resolving after this provider moved into the shared package. Other apps should pass
 * their own `prefix` so the two sites never read each other's settings when served
 * from the same origin.
 */
export const DEFAULT_LOCAL_SETTINGS_PREFIX = 'sizzlenz_';
interface LocalSettingsContextValue {
  get: <T = string>(key: string, storage?: StorageType) => T | null;
  set: <T = string>(key: string, value: T, storage?: StorageType) => void;
  remove: (key: string, storage?: StorageType) => void;
  query: (prefix: string, storage?: StorageType) => Record<string, string>;
  clear: (storage?: StorageType) => void;
}
const LocalSettingsContext = createContext<LocalSettingsContextValue | undefined>(undefined);
export interface LocalSettingsProviderProps {
  children: React.ReactNode;
  /** Key prefix applied to every stored value. Defaults to {@link DEFAULT_LOCAL_SETTINGS_PREFIX}. */
  prefix?: string;
}
export function LocalSettingsProvider({
  children,
  prefix: SETTINGS_PREFIX = DEFAULT_LOCAL_SETTINGS_PREFIX
}: LocalSettingsProviderProps) {
  const getStorage = useCallback((type: StorageType = 'local'): Storage | null => {
    if (typeof window === 'undefined') return null;
    return type === 'local' ? window.localStorage : window.sessionStorage;
  }, []);
  const get = useCallback(<T = string,>(key: string, storage: StorageType = 'local'): T | null => {
    const storageObj = getStorage(storage);
    if (!storageObj) return null;
    try {
      const item = storageObj.getItem(`${SETTINGS_PREFIX}${key}`);
      if (item === null) return null;

      // Try to parse as JSON, fallback to string
      try {
        return JSON.parse(item) as T;
      } catch {
        return item as T;
      }
    } catch (error) {
      console.error(`Error reading from ${storage}Storage:`, error);
      return null;
    }
  }, [getStorage, SETTINGS_PREFIX]);
  const set = useCallback(<T = string,>(key_0: string, value: T, storage_0: StorageType = 'local'): void => {
    const storageObj_0 = getStorage(storage_0);
    if (!storageObj_0) return;
    try {
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);
      storageObj_0.setItem(`${SETTINGS_PREFIX}${key_0}`, serialized);
    } catch (error_0) {
      console.error(`Error writing to ${storage_0}Storage:`, error_0);
    }
  }, [getStorage, SETTINGS_PREFIX]);
  const remove = useCallback((key_1: string, storage_1: StorageType = 'local'): void => {
    const storageObj_1 = getStorage(storage_1);
    if (!storageObj_1) return;
    try {
      storageObj_1.removeItem(`${SETTINGS_PREFIX}${key_1}`);
    } catch (error_1) {
      console.error(`Error removing from ${storage_1}Storage:`, error_1);
    }
  }, [getStorage, SETTINGS_PREFIX]);
  const query = useCallback((prefix: string, storage_2: StorageType = 'local'): Record<string, string> => {
    const storageObj_2 = getStorage(storage_2);
    if (!storageObj_2) return {};
    const results: Record<string, string> = {};
    const fullPrefix = `${SETTINGS_PREFIX}${prefix}`;
    try {
      for (let i = 0; i < storageObj_2.length; i++) {
        const key_2 = storageObj_2.key(i);
        if (key_2 && key_2.startsWith(fullPrefix)) {
          const value_0 = storageObj_2.getItem(key_2);
          if (value_0 !== null) {
            // Remove the SETTINGS_PREFIX from the key
            const cleanKey = key_2.substring(SETTINGS_PREFIX.length);
            results[cleanKey] = value_0;
          }
        }
      }
    } catch (error_2) {
      console.error(`Error querying ${storage_2}Storage:`, error_2);
    }
    return results;
  }, [getStorage, SETTINGS_PREFIX]);
  const clear = useCallback((storage_3: StorageType = 'local'): void => {
    const storageObj_3 = getStorage(storage_3);
    if (!storageObj_3) return;
    try {
      const keysToRemove: string[] = [];
      for (let i_0 = 0; i_0 < storageObj_3.length; i_0++) {
        const key_3 = storageObj_3.key(i_0);
        if (key_3 && key_3.startsWith(SETTINGS_PREFIX)) {
          keysToRemove.push(key_3);
        }
      }
      keysToRemove.forEach(key_4 => storageObj_3.removeItem(key_4));
    } catch (error_3) {
      console.error(`Error clearing ${storage_3}Storage:`, error_3);
    }
  }, [getStorage, SETTINGS_PREFIX]);
  const value_1 = useMemo(() => ({
    get,
    set,
    remove,
    query,
    clear
  }), [get, set, remove, query, clear]);
  return <LocalSettingsContext.Provider value={value_1}>{children}</LocalSettingsContext.Provider>;
}
export function useLocalSettings() {
  const context = useContext(LocalSettingsContext);
  if (context === undefined) {
    throw new Error("useLocalSettings must be used within a LocalSettingsProvider");
  }
  return context;
}